import { z } from "zod/v4";

import { Tuple } from "../Tuple";
import { ZSet } from "../ZSet";
import { type LinearOperatorLogic } from "./LinearOperatorLogic";
import {
	type JsonPathRef,
	type LinearComparisonOperator,
	type Literal,
	jsonpathRefSchema,
	linearComparisonOperatorSchema,
	literalSchema,
} from "./schema-util";

export function where<T>(spec: WhereSpec): LinearOperatorLogic<T, T, null> {
	// Some cases:
	//   WHERE amount > 100
	//   WHERE salary > age * 1_000
	//   WHERE hash(email) % 2 = 0
	//
	// NOT allowed, requires JOIN:
	//   WHERE o.cust_id = vip.id

	const predicate = makePredicate<T>(spec);
	return (input: ZSet<T>, state: null): [ZSet<T>, null] => {
		let output = ZSet.empty<T>();

		for (const tuple of input) {
			if (predicate(tuple.tuple)) {
				output = output.add(tuple);
			}
		}
		return [output, state];
	};
}

export type Predicate<T> = (row: Tuple<T>) => boolean;
export const makePredicate = <T>(spec: WhereSpec): Predicate<T> => {
	const { left: leftExpr, op, right: rightExpr } = spec.comparison;
	return (row) => {
		const left =
			leftExpr.type === "jsonpath-table-ref"
				? row.pluck(leftExpr.path)
				: Tuple.from<unknown>(crypto.randomUUID(), row.getOwner(), leftExpr.value);
		const right: Tuple<unknown> =
			rightExpr.type === "jsonpath-table-ref"
				? row.pluck(rightExpr.path)
				: Tuple.from<unknown>(crypto.randomUUID(), row.getOwner(), rightExpr.value);
		return left.compare(op, right);
	};
};

//
// Where spec.
//

export const whereSpecSchema = z.object({
	type: z.literal("where"),
	comparison: z.object({
		left: z.union([jsonpathRefSchema, literalSchema]),
		op: linearComparisonOperatorSchema,
		right: z.union([jsonpathRefSchema, literalSchema]),
	}),
});
export type WhereSpec = z.infer<typeof whereSpecSchema>;

export const whereSpec = (
	left: Literal | JsonPathRef,
	op: LinearComparisonOperator,
	right: Literal | JsonPathRef
): WhereSpec => ({ type: "where", comparison: { left, op, right } });
